import {
    Box,
    Flex,
    Heading,
    Icon,
    Icons,
    Stack,
    ThemeColorVariable,
    ThemeFontSizeVariable,
    ThemeIconSizeVariable,
    ThemeSpaceVariable,
    ToggleButton,
} from "@caspeco/casper-ui-library";
import { ConflictAwareActivityTimeSlot } from "components/selection-components/select-activity/conflict-aware-activity-time-slot";
import { isSameWaitListSlot } from "components/selection-components/select-activity/wait-list-utils";
import { useSectionNameMapping } from "components/selection-components/summary/use-section-name-mapping";
import TimeSlotGrid, { TIME_SLOT_GRID_COLUMN_COUNT } from "components/time-slot-grid";
import WaitListTimeSlot from "components/wait-list/wait-list-time-slot";
import { useWebBooking } from "api/api-hooks/use-web-booking";
import { useBookingStateContext } from "hooks/use-booking-state";
import testId from "types/test-id";
import { Time } from "store/store-types";
import { Article } from "types/article";
import { TimeSlot } from "types/time-slot";
import { WebRule } from "types/web-rule";
import { sanitizeTrackingId } from "logger/click-tracking";
import { SafeHTML } from "components/safe-html";
import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { isTimeBookable, isTimeWaitListable } from "utils/time-slot-filters";

const COLLAPSED_VISIBLE_ROWS = 3;
const COLLAPSED_SLOT_COUNT = COLLAPSED_VISIBLE_ROWS * TIME_SLOT_GRID_COLUMN_COUNT;

type TimeSlotGridItem =
    | { type: "time"; time: TimeSlot }
    | { type: "booked-activity"; article: Article; key: string };

interface InteractiveWebRuleInlineProps {
    webRule: WebRule;
    selectedTime: TimeSlot | null;
    onTimeClick: (time: TimeSlot) => void;
    selectedWaitListTimes: TimeSlot[];
    onWaitListTimeClick: (time: TimeSlot) => void;
    timesToNudge: Time[];
    showStartTimeHeading?: boolean;
    hideWebRuleHeader?: boolean;
    ignoredRuleIds?: Set<number>;
    chainConflictStartTimes?: Set<string>;
}

/**
 * Component for rendering an interactive webRule in inline mode (not accordion)
 * Shows title, optional comment, and time slot grid
 */
function InteractiveWebRuleInline({
    webRule,
    selectedTime,
    onTimeClick,
    selectedWaitListTimes,
    onWaitListTimeClick,
    timesToNudge,
    showStartTimeHeading,
    hideWebRuleHeader,
    ignoredRuleIds,
    chainConflictStartTimes,
}: InteractiveWebRuleInlineProps) {
    const { t } = useTranslation();
    const { guests: stateGuests, isBookingRequest } = useBookingStateContext();
    const { data: booking } = useWebBooking();
    const sectionNameMap = useSectionNameMapping();
    const gridId = useId();
    const [isExpanded, setIsExpanded] = useState(false);
    const guests = stateGuests ?? booking?.guests ?? 0;
    const sanitizedRuleTitle = sanitizeTrackingId(webRule.title);
    const titleText = showStartTimeHeading ? t("experienceView.chooseStartTime") : webRule.title;
    const isWaitListSlot = (time: TimeSlot) =>
        !isBookingRequest && isTimeWaitListable(time, guests) && !isTimeBookable(time, guests);
    const bookableTimes = webRule.times.filter((time) => !isWaitListSlot(time));
    const firstBookableTime = bookableTimes[0];
    const lastBookableTime = bookableTimes[bookableTimes.length - 1];
    const bookableRuleIds = new Set(bookableTimes.map((time) => time.ruleId || webRule.id));
    const bookedActivityMarkers =
        firstBookableTime && lastBookableTime
            ? (booking?.articles ?? []).filter((article) => {
                  if (bookableRuleIds.has(article.ruleId) || ignoredRuleIds?.has(article.ruleId)) {
                      return false;
                  }

                  return (
                      article.start.isBefore(firstBookableTime.start) ||
                      article.start.isAfter(lastBookableTime.start)
                  );
              })
            : [];
    const gridItems: TimeSlotGridItem[] = [
        ...webRule.times.map((time) => ({ type: "time" as const, time })),
        ...bookedActivityMarkers.map((article, index) => ({
            type: "booked-activity" as const,
            article,
            key: `${article.ruleId}-${article.start.format("HHmm")}-${index}`,
        })),
    ].sort((firstItem, secondItem) => {
        const firstStart =
            firstItem.type === "time" ? firstItem.time.start : firstItem.article.start;
        const secondStart =
            secondItem.type === "time" ? secondItem.time.start : secondItem.article.start;

        return firstStart.valueOf() - secondStart.valueOf();
    });
    const hasOverflowingRows = gridItems.length > COLLAPSED_SLOT_COUNT;
    const visibleItems =
        hasOverflowingRows && !isExpanded ? gridItems.slice(0, COLLAPSED_SLOT_COUNT) : gridItems;
    const expandButtonText = isExpanded ? t("showMoreText.showLess") : t("showMoreText.showMore");

    const renderBookedActivityMarker = (article: Article, key: string) => {
        const timeString = article.start.format("HH:mm");
        const sectionName =
            sectionNameMap.get(article.ruleId)?.name || article.articleGroupName || article.name;

        return (
            <ToggleButton
                key={`booked-activity-${key}`}
                aria-label={`${timeString} - ${sectionName}`}
                size="lg"
                h={ThemeSpaceVariable.XLarge}
                isDisabled
                subText={sectionName}
                textAlign="center"
            >
                {timeString}
            </ToggleButton>
        );
    };

    const renderGridItem = (item: TimeSlotGridItem) => {
        if (item.type === "booked-activity") {
            return renderBookedActivityMarker(item.article, item.key);
        }

        const { time } = item;
        if (isWaitListSlot(time)) {
            const selectedTimes = selectedWaitListTimes.filter((selectedTime) =>
                isSameWaitListSlot(selectedTime, time)
            );

            return (
                <WaitListTimeSlot
                    key={`waitlist-${time.ruleId}-${time.start.format("HHmm")}`}
                    time={time}
                    selectedTimes={selectedTimes}
                    onTimeClick={onWaitListTimeClick}
                    trackingId={`select-waitlist-${sanitizedRuleTitle}-${time.start.format("HHmm")}`}
                    disableAnimation
                />
            );
        }

        return (
            <ConflictAwareActivityTimeSlot
                key={`bookable-${time.ruleId}-${time.start.format("HHmm")}`}
                time={time}
                webRuleId={time.ruleId || webRule.id}
                selectedTime={selectedTime ?? undefined}
                onClick={onTimeClick}
                timesToNudge={timesToNudge}
                trackingId={`select-time-${sanitizedRuleTitle}-${time.start.format("HHmm")}`}
                times={bookableTimes}
                ignoredRuleIds={ignoredRuleIds}
                chainConflictStartTimes={chainConflictStartTimes}
                renderPaddingArticles={false}
            />
        );
    };

    const renderTimeSlotGrid = (items: TimeSlotGridItem[], id?: string) => (
        <Box id={id} position="relative">
            <TimeSlotGrid items={items.map(renderGridItem)} />
            {id && hasOverflowingRows && !isExpanded && (
                <Box
                    position="absolute"
                    left={0}
                    right={0}
                    bottom={0}
                    h="64px"
                    pointerEvents="none"
                    background={`linear-gradient(to bottom, rgba(255, 255, 255, 0), ${ThemeColorVariable.Surface} 82%)`}
                />
            )}
        </Box>
    );

    return (
        <Box>
            {!hideWebRuleHeader && (
                <Stack spacing={ThemeSpaceVariable.XSmall} mb={ThemeSpaceVariable.Small}>
                    <Heading
                        as="h3"
                        fontSize={ThemeFontSizeVariable.Large}
                        color={ThemeColorVariable.OnBackgroundHeader}
                        fontWeight="medium"
                    >
                        {titleText}
                    </Heading>
                    {webRule.comment && (
                        <SafeHTML html={webRule.comment} data-testid={testId.timeSet.comment} />
                    )}
                </Stack>
            )}
            {renderTimeSlotGrid(visibleItems, gridId)}
            {hasOverflowingRows && (
                <Flex justify="center" mt={ThemeSpaceVariable.XSmall}>
                    <Box
                        as="button"
                        type="button"
                        aria-label={expandButtonText}
                        aria-expanded={isExpanded}
                        aria-controls={gridId}
                        data-track-click={
                            isExpanded ? "collapse-activity-times" : "expand-activity-times"
                        }
                        onClick={() => setIsExpanded((currentValue) => !currentValue)}
                        display="inline-flex"
                        alignItems="center"
                        justifyContent="center"
                        w="44px"
                        h="44px"
                        color={ThemeColorVariable.OnSurface}
                        background="transparent"
                        border="0"
                        borderRadius="full"
                        cursor="pointer"
                    >
                        <Icon
                            icon={isExpanded ? Icons.Collapse : Icons.Expand}
                            size={ThemeIconSizeVariable.Large}
                        />
                    </Box>
                </Flex>
            )}
        </Box>
    );
}

export default InteractiveWebRuleInline;
